/**
 * Validates that a redirect URL is safe for local navigation.
 * Only allows relative paths starting with a single '/' to prevent open redirect vulnerabilities.
 *
 * @param redirectUrl - The redirect URL to validate
 * @param fallbackUrl - The URL to return if validation fails (default: '/redirect')
 * @returns A safe redirect URL or the fallback URL
 *
 * @example
 * validateRedirectUrl('/dashboard') // returns '/dashboard'
 * validateRedirectUrl('//evil.com') // returns '/redirect'
 * validateRedirectUrl('https://evil.com') // returns '/redirect'
 * validateRedirectUrl('javascript:alert(1)') // returns '/redirect'
 */
export function validateRedirectUrl(
  redirectUrl: string | null | undefined,
  fallbackUrl = '/redirect',
): string {
  // Return fallback if no redirect URL provided
  if (!redirectUrl || typeof redirectUrl !== 'string') {
    return fallbackUrl;
  }

  // Trim whitespace
  const trimmedUrl = redirectUrl.trim();

  // Reject empty strings after trimming
  if (!trimmedUrl) {
    return fallbackUrl;
  }

  // Must start with exactly one '/' (relative path)
  if (!trimmedUrl.startsWith('/')) {
    return fallbackUrl;
  }

  // Reject protocol-relative URLs (//example.com)
  if (trimmedUrl.startsWith('//')) {
    return fallbackUrl;
  }

  // Reject URLs with backslashes (potential bypass attempt)
  if (trimmedUrl.includes('\\')) {
    return fallbackUrl;
  }

  // Try to parse as URL with current origin to detect absolute URLs
  try {
    const testUrl = new URL(trimmedUrl, window.location.origin);

    // Ensure the parsed URL is from the same origin
    if (testUrl.origin !== window.location.origin) {
      return fallbackUrl;
    }

    // Ensure no protocol other than http/https (reject javascript:, data:, etc.)
    if (testUrl.protocol !== 'http:' && testUrl.protocol !== 'https:') {
      return fallbackUrl;
    }
  } catch {
    // If URL parsing fails, reject it
    return fallbackUrl;
  }

  // Additional check: reject if it contains common protocol patterns
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:', 'about:', 'blob:'];

  for (const protocol of dangerousProtocols) {
    if (trimmedUrl.toLowerCase().includes(protocol)) {
      return fallbackUrl;
    }
  }

  // All checks passed, return the validated URL
  return trimmedUrl;
}
